import { Accordion, ActionIcon, Badge, Button, Group, Modal, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { IconCheck, IconPencil } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { type Schemas, api, call, hasPermission } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, Loading, notifyError, notifyOk } from "../components/common";

export function FactoriesPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure();
  const [form, setForm] = useState({ name: "", code: "", timezone: "Asia/Kolkata", address: "" });
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const create = useMutation({
    mutationFn: () => call(api.POST("/api/v1/factories", { body: { ...form, address: form.address || null } })),
    onSuccess: () => { notifyOk("Factory created"); void qc.invalidateQueries({ queryKey: ["factories"] }); close(); },
    onError: notifyError,
  });
  // Creating factories needs a company-wide grant; company admins have it.
  const canCreate = me?.roles.includes("company_admin");

  if (factories.isLoading) return <Loading />;
  if (factories.error) return <ErrorAlert error={factories.error} />;
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Factories, departments & lines</Title>
        {canCreate && <Button onClick={open}>New factory</Button>}
      </Group>
      <Accordion variant="separated">
        {factories.data!.map((f) => (
          <Accordion.Item key={f.id} value={f.id}>
            <Accordion.Control>
              <Text fw={600}>{f.name}</Text>
              <Text size="xs" c="dimmed">{f.code} · {f.timezone}{f.address ? ` · ${f.address}` : ""}</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Departments factoryId={f.id} canManage={hasPermission(me, "factory:manage")} />
                <Lines factoryId={f.id} canManage={hasPermission(me, "factory:manage")} />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
      <Modal opened={opened} onClose={close} title="New factory">
        <Stack>
          <TextInput label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <TextInput label="Code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.currentTarget.value })} />
          <TextInput label="Time zone" description="IANA name, e.g. Asia/Kolkata, Europe/Berlin, America/Chicago"
            value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.currentTarget.value })} />
          <TextInput label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.currentTarget.value })} />
          <Button loading={create.isPending} onClick={() => create.mutate()}>Create</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

function DepartmentRow({ factoryId, dep, canManage }: { factoryId: string; dep: Schemas["DepartmentOut"]; canManage: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => call(api.PATCH("/api/v1/factories/{factory_id}/departments/{department_id}", {
      params: { path: { factory_id: factoryId, department_id: dep.id } }, body: { name: name ?? dep.name },
    })),
    onSuccess: () => { setName(null); void qc.invalidateQueries({ queryKey: ["departments", factoryId] }); },
    onError: notifyError,
  });
  return (
    <Table.Tr>
      <Table.Td w={120}>{dep.code}</Table.Td>
      <Table.Td>
        {name === null ? dep.name : <TextInput size="xs" value={name} onChange={(e) => setName(e.currentTarget.value)} />}
      </Table.Td>
      <Table.Td w={40}>
        {canManage && (name === null
          ? <ActionIcon variant="subtle" aria-label={`Rename ${dep.name}`} onClick={() => setName(dep.name)}><IconPencil size={16} /></ActionIcon>
          : <ActionIcon variant="subtle" aria-label="Save name" loading={save.isPending} onClick={() => save.mutate()}><IconCheck size={16} /></ActionIcon>)}
      </Table.Td>
    </Table.Tr>
  );
}

function Lines({ factoryId, canManage }: { factoryId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const path = { factory_id: factoryId };
  const [form, setForm] = useState({ name: "", code: "", department_id: "", shift_pattern_id: "" });
  const lines = useQuery({ queryKey: ["lines", factoryId], queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/lines", { params: { path } })) });
  const deps = useQuery({ queryKey: ["departments", factoryId], queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/departments", { params: { path } })) });
  const patterns = useQuery({ queryKey: ["shift-patterns", factoryId], queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/shift-patterns", { params: { path } })) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["lines", factoryId] });
  const add = useMutation({
    mutationFn: () => call(api.POST("/api/v1/factories/{factory_id}/lines", {
      params: { path }, body: { name: form.name, code: form.code, department_id: form.department_id || null, shift_pattern_id: form.shift_pattern_id || null },
    })),
    onSuccess: () => { setForm({ name: "", code: "", department_id: "", shift_pattern_id: "" }); refresh(); },
    onError: notifyError,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Schemas["LineUpdate"] }) =>
      call(api.PATCH("/api/v1/lines/{line_id}", { params: { path: { line_id: id } }, body })),
    onSuccess: refresh,
    onError: notifyError,
  });
  const depName = (id?: string | null) => deps.data?.find((d) => d.id === id)?.name ?? "—";
  const patternOptions = (patterns.data ?? []).map((p) => ({ value: p.id, label: p.name }));
  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">Production lines</Text>
      <Table>
        <Table.Tbody>
          {(lines.data ?? []).map((l) => (
            <Table.Tr key={l.id} opacity={l.is_active ? 1 : 0.5}>
              <Table.Td w={120}>{l.code}</Table.Td>
              <Table.Td>{l.name} {!l.is_active && <Badge size="xs" color="gray">inactive</Badge>}</Table.Td>
              <Table.Td>{depName(l.department_id)}</Table.Td>
              <Table.Td w={220}>
                <Select size="xs" disabled={!canManage} clearable placeholder="factory default shifts" data={patternOptions}
                  value={l.shift_pattern_id ?? null} onChange={(v) => update.mutate({ id: l.id, body: { shift_pattern_id: v } })} />
              </Table.Td>
              <Table.Td w={100}>
                {canManage && <Button size="compact-xs" variant="default" onClick={() => update.mutate({ id: l.id, body: { is_active: !l.is_active } })}>
                  {l.is_active ? "Deactivate" : "Activate"}</Button>}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {lines.data?.length === 0 && <Text size="sm" c="dimmed">No lines yet. Lines group machines and may follow their own shift pattern.</Text>}
      {canManage && (
        <Group align="end">
          <TextInput size="xs" label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.currentTarget.value })} />
          <TextInput size="xs" label="Line name" value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <Select size="xs" label="Department" clearable data={(deps.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
            value={form.department_id || null} onChange={(v) => setForm({ ...form, department_id: v ?? "" })} />
          <Button size="xs" disabled={!form.name || !form.code} loading={add.isPending} onClick={() => add.mutate()}>Add line</Button>
        </Group>
      )}
    </Stack>
  );
}

function Departments({ factoryId, canManage }: { factoryId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const deps = useQuery({
    queryKey: ["departments", factoryId],
    queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/departments", { params: { path: { factory_id: factoryId } } })),
  });
  const add = useMutation({
    mutationFn: () => call(api.POST("/api/v1/factories/{factory_id}/departments", {
      params: { path: { factory_id: factoryId } }, body: { name, code },
    })),
    onSuccess: () => { setName(""); setCode(""); void qc.invalidateQueries({ queryKey: ["departments", factoryId] }); },
    onError: notifyError,
  });
  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">Departments</Text>
      <Table>
        <Table.Tbody>
          {(deps.data ?? []).map((d) => (
            <DepartmentRow key={d.id} factoryId={factoryId} dep={d} canManage={canManage} />
          ))}
        </Table.Tbody>
      </Table>
      {deps.data?.length === 0 && <Text size="sm" c="dimmed">No departments yet.</Text>}
      {canManage && (
        <Group align="end">
          <TextInput size="xs" label="Code" value={code} onChange={(e) => setCode(e.currentTarget.value)} />
          <TextInput size="xs" label="Department name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Button size="xs" disabled={!name || !code} loading={add.isPending} onClick={() => add.mutate()}>Add</Button>
        </Group>
      )}
    </Stack>
  );
}
